import { IAPIRequestMetaData } from "redux/_core/api/types";
import { getType } from "typesafe-actions";
import reportActions from "./actions";
import {
	IBilgeWaterRecord,
	ICellRangeUpdatePayload,
	ICNRRangeVessel,
	IGarbageDischargeAllVesselsRecord,
	IGarbageCustomSearchRecord,
	IGarbageRawRecord,
	ILoadCNRRangeVesselListParams,
	IEmissionsAllVesselAggregatedData,
	IEmissionsVesselSpecificAggregatedData,
	IPackagingAllVesselAggregatedData,
	IPackagingVesselSpecificAggregatedData,
	ICleaningChemicalsAllVesselAggregatedData,
	ICleaningChemicalsVesselSpecificAggregatedData,
	IRefrigerantAllVesselAggregatedData,
	IRefrigerantVesselSpecificAggregatedData,
	IRefrigerantCustomSearchRecord,
	IBilgeWaterCustomSearchRecord,
	IPackagingCustomSearchRecord,
	ICleaningChemicalsCustomSearchRecord,
	IORSCustomSearchRecord,
	ICargoSlopCustomSearchRecord,
	IEmissionsCustomSearchRecord,
} from "./@types";
import commonUtils from "redux/_common/utils";
const baseAppUrl = `${process.env.REACT_APP_DOMAIN}`;

const reportAPIS = {
	// Environmental Reports
	loadEmissionsDataPerVessel(
		vesselId: string,
		yearRange: string,
		monthRange: string,
	): IAPIRequestMetaData<IEmissionsVesselSpecificAggregatedData[]> {
		return {
			url: `https://analytics.wayship.io/api/v1/marpol-emf-emissions/vessel/${vesselId}`,
			feature: getType(
				reportActions.commands.emissionsVesselSpecificAggregateDataLoad,
			),
			method: "GET",
			queryParams: {
				yearRange,
				monthRange,
			},
		};
	},
	loadEmissionsAllVesselAggregatedData(
		tenantId: string,
		year: string,
		monthRange: string,
	): IAPIRequestMetaData<IEmissionsAllVesselAggregatedData[]> {
		return {
			url: `https://analytics.wayship.io/api/v1/marpol-emf-emissions/${tenantId}`,
			feature: getType(
				reportActions.commands.emissionsAllVesselAggregateDataLoad,
			),
			method: "GET",
			queryParams: {
				year,
				monthRange,
			},
		};
	},
	loadPackagingDataPerVessel(
		vesselId: string,
		yearRange: string,
		monthRange: string,
	): IAPIRequestMetaData<IPackagingVesselSpecificAggregatedData[]> {
		return {
			url: `https://analytics.wayship.io/api/v1/marpol-emf-packaging/vessel/${vesselId}`,
			feature: getType(
				reportActions.commands.packagingVesselSpecificAggregateDataLoad,
			),
			method: "GET",
			queryParams: {
				yearRange,
				monthRange,
			},
		};
	},
	loadPackagingAllVesselAggregatedData(
		tenantId: string,
		year: string,
		monthRange: string,
	): IAPIRequestMetaData<IPackagingAllVesselAggregatedData[]> {
		return {
			url: `https://analytics.wayship.io/api/v1/marpol-emf-packaging/${tenantId}`,
			feature: getType(
				reportActions.commands.packagingAllVesselAggregateDataLoad,
			),
			method: "GET",
			queryParams: {
				year,
				monthRange,
			},
		};
	},
	loadCleaningChemicalsDataPerVessel(
		vesselId: string,
		yearRange: string,
		monthRange: string,
	): IAPIRequestMetaData<ICleaningChemicalsVesselSpecificAggregatedData[]> {
		return {
			url: `https://analytics.wayship.io/api/v1/marpol-emf-cleaning-chemicals/vessel/${vesselId}`,
			feature: getType(
				reportActions.commands.cleaningChemicalsVesselSpecificAggregateDataLoad,
			),
			method: "GET",
			queryParams: {
				yearRange,
				monthRange,
			},
		};
	},
	loadCleaningChemicalsAllVesselAggregatedData(
		tenantId: string,
		year: string,
		monthRange: string,
	): IAPIRequestMetaData<ICleaningChemicalsAllVesselAggregatedData[]> {
		return {
			url: `https://analytics.wayship.io/api/v1/marpol-emf-cleaning-chemicals/${tenantId}`,
			feature: getType(
				reportActions.commands.cleaningChemicalsAllVesselAggregateDataLoad,
			),
			method: "GET",
			queryParams: {
				year,
				monthRange,
			},
		};
	},
	loadRefrigerantDataPerVessel(
		vesselId: string,
		yearRange: string,
		monthRange: string,
	): IAPIRequestMetaData<IRefrigerantVesselSpecificAggregatedData[]> {
		return {
			url: `https://analytics.wayship.io/api/v1/marpol-emf-refrigerant/vessel/${vesselId}`,
			feature: getType(
				reportActions.commands.refrigerantVesselSpecificAggregateDataLoad,
			),
			method: "GET",
			queryParams: {
				yearRange,
				monthRange,
			},
		};
	},
	loadRefrigerantAllVesselAggregatedData(
		tenantId: string,
		year: string,
		monthRange: string,
	): IAPIRequestMetaData<IRefrigerantAllVesselAggregatedData[]> {
		return {
			url: `https://analytics.wayship.io/api/v1/marpol-emf-refrigerant/${tenantId}`,
			feature: getType(
				reportActions.commands.refrigerantAllVesselAggregateDataLoad,
			),
			method: "GET",
			queryParams: {
				year,
				monthRange,
			},
		};
	},
	loadGarbageCustomSearch(
		tenantId: string,
		fromDate: string,
		toDate: string,
		vesselIds: string[],
	): IAPIRequestMetaData<IGarbageCustomSearchRecord[]> {
		return {
			url: `https://analytics.wayship.io/api/v1/marpol-grb/custom-search`,
			feature: getType(reportActions.commands.garbageCustomSearchLoad),
			method: "GET",
			queryParams: {
				tenantId,
				fromDate,
				toDate,
				vesselIds: vesselIds.join(","),
			},
		};
	},
	loadBilgeWaterCustomSearch(
		tenantId: string,
		fromDate: string,
		toDate: string,
		vesselIds: string[],
	): IAPIRequestMetaData<IBilgeWaterCustomSearchRecord[]> {
		return {
			url: `https://analytics.wayship.io/api/v1/marpol-bilge/custom-search`,
			feature: getType(reportActions.commands.bilgeWaterCustomSearchLoad),
			method: "GET",
			queryParams: {
				tenantId,
				fromDate,
				toDate,
				vesselIds: vesselIds.join(","),
			},
		};
	},
	loadPackagingCustomSearch(
		tenantId: string,
		fromDate: string,
		toDate: string,
		vesselIds: string[],
	): IAPIRequestMetaData<IPackagingCustomSearchRecord[]> {
		return {
			url: `https://analytics.wayship.io/api/v1/marpol-emf-packaging/custom-search`,
			feature: getType(reportActions.commands.packagingCustomSearchLoad),
			method: "GET",
			queryParams: {
				tenantId,
				fromDate,
				toDate,
				vesselIds: vesselIds.join(","),
			},
		};
	},
	loadCleaningChemicalsCustomSearch(
		tenantId: string,
		fromDate: string,
		toDate: string,
		vesselIds: string[],
	): IAPIRequestMetaData<ICleaningChemicalsCustomSearchRecord[]> {
		return {
			url: `https://analytics.wayship.io/api/v1/marpol-emf-cleaning-chemicals/custom-search`,
			feature: getType(
				reportActions.commands.cleaningChemicalsCustomSearchLoad,
			),
			method: "GET",
			queryParams: {
				tenantId,
				fromDate,
				toDate,
				vesselIds: vesselIds.join(","),
			},
		};
	},
	loadRefrigerantCustomSearch(
		tenantId: string,
		fromDate: string,
		toDate: string,
		vesselIds: string[],
	): IAPIRequestMetaData<IRefrigerantCustomSearchRecord[]> {
		return {
			url: `https://analytics.wayship.io/api/v1/marpol-emf-refrigerant/custom-search`,
			feature: getType(reportActions.commands.refrigerantCustomSearchLoad),
			method: "GET",
			queryParams: {
				tenantId,
				fromDate,
				toDate,
				vesselIds: vesselIds.join(","),
			},
		};
	},
	loadORSCustomSearch(
		tenantId: string,
		fromDate: string,
		toDate: string,
		vesselIds: string[],
	): IAPIRequestMetaData<IORSCustomSearchRecord[]> {
		return {
			url: `https://analytics.wayship.io/api/v1/marpol-ors/custom-search`,
			feature: getType(reportActions.commands.orsCustomSearchLoad),
			method: "GET",
			queryParams: {
				tenantId,
				fromDate,
				toDate,
				vesselIds: vesselIds.join(","),
			},
		};
	},
	loadCargoSlopCustomSearch(
		tenantId: string,
		fromDate: string,
		toDate: string,
		vesselIds: string[],
	): IAPIRequestMetaData<ICargoSlopCustomSearchRecord[]> {
		return {
			url: `https://analytics.wayship.io/api/v1/marpol-cargo-slop/custom-search`,
			feature: getType(reportActions.commands.cargoSlopCustomSearchLoad),
			method: "GET",
			queryParams: {
				tenantId,
				fromDate,
				toDate,
				vesselIds: vesselIds.join(","),
			},
		};
	},
	loadEmissionsCustomSearch(
		tenantId: string,
		fromDate: string,
		toDate: string,
		vesselIds: string[],
	): IAPIRequestMetaData<IEmissionsCustomSearchRecord[]> {
		return {
			url: `https://analytics.wayship.io/api/v1/marpol-emf-emissions/custom-search`,
			feature: getType(reportActions.commands.emissionsCustomSearchLoad),
			method: "GET",
			queryParams: {
				tenantId,
				fromDate,
				toDate,
				vesselIds: vesselIds.join(","),
			},
		};
	},
	// Waste and Oil Resiude and Sludge Reports
	loadGarbageReport(
		tenantId: string,
		vesselId: string,
		yearRange: string,
		monthRange: string,
	): IAPIRequestMetaData<IGarbageRawRecord[]> {
		return {
			url: `https://analytics.wayship.io/api/v1/marpol-grb/vessel/${vesselId}`,
			feature: getType(reportActions.commands.garbageReportLoad),
			method: "GET",
			queryParams: {
				yearRange,
				monthRange,
			},
		};
	},
	loadGarbageTypeForAllVessels(
		tenantId: string,
		type: "dischargeMethod" | "garbageCategory",
		year: string,
		monthRange: string,
	): IAPIRequestMetaData<IGarbageDischargeAllVesselsRecord[]> {
		return {
			url: `https://analytics.wayship.io/api/v1/marpol-grb/${tenantId}`,
			feature: getType(reportActions.commands.garbageDischargeAllVesselsLoad),
			method: "GET",
			queryParams: {
				year,
				monthRange,
				type,
			},
		};
	},
	loadBilgeWaterReport(
		tenantId: string,
		vesselId: string,
		yearRange: string,
		monthRange: string,
	): IAPIRequestMetaData<IBilgeWaterRecord[]> {
		return {
			url: `https://analytics.wayship.io/api/v1/marpol-bilge/vessel/${vesselId}`,
			feature: getType(reportActions.commands.bilgeWaterReportLoad),
			method: "GET",
			queryParams: {
				yearRange,
				monthRange,
			},
		};
	},
	loadBilgeWaterAllVesselsAggregate(
		tenantId: string,
		year: string,
		monthRange: string,
	): IAPIRequestMetaData<any> {
		return {
			url: `https://analytics.wayship.io/api/v1/marpol-bilge/${tenantId}`,
			feature: getType(
				reportActions.commands.bilgeWaterAllVesselsAggregateLoad,
			),
			method: "GET",
			queryParams: {
				year,
				monthRange,
			},
		};
	},
	loadOrsAllVesselsAggregate(
		tenantId: string,
		year: string,
		monthRange: string,
	): IAPIRequestMetaData<any> {
		return {
			url: `https://analytics.wayship.io/api/v1/marpol-ors/${tenantId}`,
			feature: getType(reportActions.commands.orsAllVesselAggregateLoad),
			method: "GET",
			queryParams: {
				year,
				monthRange,
			},
		};
	},
	loadOrsVesselSpecificAggregate(
		tenantId: string,
		year: string,
		monthRange: string,
		vesselId: string,
	): IAPIRequestMetaData<any> {
		return {
			url: `https://analytics.wayship.io/api/v1/marpol-ors/vessel/${vesselId}`,
			feature: getType(reportActions.commands.orsVesselSpecificAggregateLoad),
			method: "GET",
			queryParams: {
				yearRange: year,
				monthRange,
			},
		};
	},
	loadCargoSlopAllVesselsAggregate(
		tenantId: string,
		year: string,
		monthRange: string,
	): IAPIRequestMetaData<any> {
		return {
			url: `https://analytics.wayship.io/api/v1/marpol-cargo-slop/${tenantId}`,
			feature: getType(reportActions.commands.cargoSlopAllVesselAggregateLoad),
			method: "GET",
			queryParams: {
				year,
				monthRange,
			},
		};
	},
	loadCargoSlopVesselSpecificAggregate(
		tenantId: string,
		year: string,
		monthRange: string,
		vesselId: string,
	): IAPIRequestMetaData<any> {
		return {
			url: `https://analytics.wayship.io/api/v1/marpol-cargo-slop/vessel/${vesselId}`,
			feature: getType(
				reportActions.commands.cargoSlopVesselSpecificAggregateLoad,
			),
			method: "GET",
			queryParams: {
				yearRange: year,
				monthRange,
			},
		};
	},
	loadOilSpillsInWaterAllVesselsAggregate(
		tenantId: string,
		year: string,
		monthRange: string,
	): IAPIRequestMetaData<any> {
		return {
			url: `https://analytics.wayship.io/api/v1/marpol-oil-spill/${tenantId}`,
			feature: getType(
				reportActions.commands.oilSpillsInWaterAllVesselAggregateLoad,
			),
			method: "GET",
			queryParams: {
				year,
				monthRange,
			},
		};
	},
	loadOilSpillsInWaterVesselSpecificAggregate(
		tenantId: string,
		year: string,
		monthRange: string,
		vesselId: string,
	): IAPIRequestMetaData<any> {
		return {
			url: `https://analytics.wayship.io/api/v1/marpol-oil-spill/vessel/${vesselId}`,
			feature: getType(
				reportActions.commands.oilSpillsInWaterVesselSpecificAggregateLoad,
			),
			method: "GET",
			queryParams: {
				yearRange: year,
				monthRange,
			},
		};
	},
	loadEmfFleetUsageReport(
		tenantId: string,
		startOfMonth: string,
		endOfMonth: string,
	): IAPIRequestMetaData<any> {
		return {
			url: `https://analytics.wayship.io/api/v1/marpol-emf-fleetusage/${tenantId}`,
			feature: getType(reportActions.commands.emfFleetUsageDataLoad),
			method: "GET",
			queryParams: {
				from: startOfMonth,
				to: endOfMonth,
			},
		};
	},
	loadBiofouling1FleetReport(
		tenantId: string,
		startOfMonth: string,
		endOfMonth: string,
	): IAPIRequestMetaData<any> {
		return {
			url: `https://analytics.wayship.io/api/v1/marpol-bfl1-fleetusage/${tenantId}`,
			feature: getType(reportActions.commands.biofouling1FleetDataLoad),
			method: "GET",
			queryParams: {
				from: startOfMonth,
				to: endOfMonth,
			},
		};
	},
	loadBiofouling2FleetReport(
		tenantId: string,
		startOfMonth: string,
		endOfMonth: string,
	): IAPIRequestMetaData<any> {
		return {
			url: `https://analytics.wayship.io/api/v1/marpol-bfl2-fleetusage/${tenantId}`,
			feature: getType(reportActions.commands.biofouling2FleetDataLoad),
			method: "GET",
			queryParams: {
				from: startOfMonth,
				to: endOfMonth,
			},
		};
	},
	loadComprehensiveNoonReportAllVessels(): // tenantId: string,
	// vesselType: string,
	// vesselClass: string
	IAPIRequestMetaData<any> {
		return {
			url: `${commonUtils.getAPIBaseUrl()}/api/cnrstatus`,
			feature: getType(
				reportActions.commands.comprehensiveNoonReportAllVesselLoad,
			),
			method: "GET",
			// queryParams: {
			// 	tenantId,
			// 	vesselType,
			// 	vesselClass
			// },
		};
	},
	loadComprehensiveNoonReportVesselSpecificData(): //-----Temporary
	// tenantId: string,
	// dayRange: string
	// vesselId: string,
	IAPIRequestMetaData<any> {
		return {
			url: `https://676e4d47df5d7dac1cca3775.mockapi.io/api/v1/cnr-`, //-----To be changed
			feature: getType(
				reportActions.commands.comprehensiveNoonReportVesselSpecificDataLoad,
			),
			method: "GET",
			// queryParams: {
			// 	tenantId,
			// vesselId: string,
			// },
		};
	},
	loadCNRRangesVesselList(
		params: ILoadCNRRangeVesselListParams,
	): IAPIRequestMetaData<ICNRRangeVessel[]> {
		const { limit, skip, action, kind, vesselIds } = params;
		return {
			// url: "https://dummyjson.com/c/3fab-29bd-49d4-84a8",
			url: `${commonUtils.getAPIBaseUrl()}/api/ranges`,
			method: "GET",
			feature: getType(reportActions.commands.cnrRangesVesselsLoad),
			queryParams: {
				limit,
				skip,
				action,
				kind,
				vesselIds,
			},
		};
	},
	loadVesselSubTypeConfig(vesselSubType: string): IAPIRequestMetaData<any> {
		return {
			url: "https://dummyjson.com/c/3fab-29bd-49d4-84a8",
			method: "GET",
			feature: getType(reportActions.commands.cnrRangesVesselSubTypeConfigLoad),
			queryParams: {
				vesselSubType,
			},
		};
	},
	updateCNRRangesRangeCell(
		data: ICellRangeUpdatePayload,
	): IAPIRequestMetaData<any> {
		return {
			url: `${commonUtils.getAPIBaseUrl()}/api/ranges`,
			method: "PATCH",
			feature: getType(reportActions.commands.cnrRangesRangeCellUpdate),
			body: {
				...data,
			},
		};
	},
	loadNavnuc(limit: number = 50, skip: number = 0): IAPIRequestMetaData<any> {
		return {
			url: `${commonUtils.getAPIBaseUrl()}/api/forms?formName=navnuc&limit=${limit}&skip=${skip}`,
			feature: getType(reportActions.commands.navnucLoad),
			method: "GET",
		};
	},
	uploadCNRRangesCSVFile(formData: FormData): IAPIRequestMetaData<any> {
		return {
			url: `${commonUtils.getAPIBaseUrl()}/api/upload-cnr-ranges`,
			method: "POST",
			feature: getType(reportActions.commands.cnrRangesCSVFileUpload),
			body: formData,
			headers: {
				"Content-Type": "multipart/form-data",
			},
		};
	},
	loadLngReport(
		vesselIds: string[],
		fromDate: string,
		toDate: string,
	): IAPIRequestMetaData<any> {
		return {
			url: `${baseAppUrl}/api/reports/lng`,
			feature: "LNG_REPORT",
			method: "POST",
			body: { vesselIds, fromDate, toDate },
		};
	},
	loadSpeedLossReport(
		vesselIds: string[],
		fromDate: string,
		toDate: string,
	): IAPIRequestMetaData<any> {
		return {
			url: `${baseAppUrl}/api/reports/speedloss`,
			feature: "SPEEDLOSS_REPORT",
			method: "POST",
			body: { vesselIds, fromDate, toDate },
		};
	},
	loadLngVesselList(): IAPIRequestMetaData<any> {
		return {
			url: `${baseAppUrl}/api/vessels`,
			feature: "LNG_VESSEL_LIST",
			method: "GET",
			queryParams: {
				filter: JSON.stringify({ limit: 500 }),
			},
		};
	},
};

export default reportAPIS;
